import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { logoSrc } from "@/components/BrandLogo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SignaturePad } from "@/components/forms/SignaturePad";
import { Camera, Images, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const CHOICES = {
  yes_no: [["yes", "Yes", "bg-emerald-600"], ["no", "No", "bg-rose-600"]],
  pass_fail: [["pass", "Pass", "bg-emerald-600"], ["fail", "Fail", "bg-rose-600"], ["na", "N/A", "bg-zinc-600"]],
};

function ChoiceButtons({ type, value, onChange, tid }) {
  return (
    <div className="flex gap-2">
      {CHOICES[type].map(([v, l, on]) => (
        <button key={v} type="button" data-testid={`${tid}-${v}`} onClick={() => onChange(value === v ? "" : v)}
          className={`flex-1 h-11 rounded-md border text-sm font-semibold transition-colors ${value === v ? `${on} text-white border-transparent` : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}>{l}</button>
      ))}
    </div>
  );
}

function PhotoInput({ value, onChange, tid }) {
  const [busy, setBusy] = useState(false);
  const list = value || [];
  const upload = async (ev) => {
    const files = [...(ev.target.files || [])]; ev.target.value = ""; if (!files.length) return;
    const added = [];
    setBusy(true);
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name}: please choose an image`); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: photo must be under 10MB`); continue; }
      try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); added.push(data.file_url); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
    if (added.length) onChange([...list, ...added]);
    setBusy(false);
  };
  const tile = "h-20 w-20 rounded-md border border-dashed border-zinc-300 flex flex-col items-center justify-center text-zinc-500 text-[11px] cursor-pointer hover:bg-zinc-50 transition-colors";
  return (
    <div className="flex flex-wrap gap-2">
      {list.map((u) => (
        <div key={u} className="relative h-20 w-20 rounded-md overflow-hidden border border-zinc-200">
          <img src={logoSrc(u)} alt="" className="h-full w-full object-cover" />
          <button type="button" onClick={() => onChange(list.filter((x) => x !== u))} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
        </div>
      ))}
      {busy ? <div className={tile}><Loader2 className="h-5 w-5 animate-spin" /></div> : <>
        <label data-testid={`${tid}-upload`} className={tile}>
          <Camera className="h-5 w-5 mb-0.5" />Take photo
          <input data-testid={`${tid}-camera-input`} type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} />
        </label>
        <label data-testid={`${tid}-gallery`} className={tile}>
          <Images className="h-5 w-5 mb-0.5" />From gallery
          <input data-testid={`${tid}-gallery-input`} type="file" accept="image/*" multiple className="hidden" onChange={upload} />
        </label>
      </>}
    </div>
  );
}

function SiteInput({ value, onChange, tid }) {
  const [sites, setSites] = useState([]);
  useEffect(() => { api.get("/sites").then((r) => setSites((r.data || []).filter((s) => !s.archived))).catch(() => {}); }, []);
  return (
    <Select value={value?.id || ""} onValueChange={(v) => onChange({ id: v, name: sites.find((s) => s.id === v)?.name })}>
      <SelectTrigger data-testid={tid}><SelectValue placeholder={value?.name || "Choose site…"} /></SelectTrigger>
      <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function FieldInput({ field, value, onChange }) {
  const tid = `field-${field.id}`;
  switch (field.type) {
    case "short_text": return <Input data-testid={tid} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    case "long_text": return <Textarea data-testid={tid} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    case "number": return <Input data-testid={tid} type="number" inputMode="decimal" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
    case "date": return <Input data-testid={tid} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    case "yes_no": case "pass_fail": return <ChoiceButtons type={field.type} value={value} onChange={onChange} tid={tid} />;
    case "dropdown": return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={tid}><SelectValue placeholder="Choose…" /></SelectTrigger>
        <SelectContent>{field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
    case "checkboxes": {
      const arr = value || [];
      return (
        <div className="space-y-2">
          {field.options.map((o, i) => (
            <label key={o} className="flex items-center gap-2 text-sm">
              <Checkbox data-testid={`${tid}-opt-${i}`} checked={arr.includes(o)} onCheckedChange={(c) => onChange(c ? [...arr, o] : arr.filter((x) => x !== o))} />{o}
            </label>
          ))}
        </div>
      );
    }
    case "site": return <SiteInput value={value} onChange={onChange} tid={tid} />;
    case "photo": return <PhotoInput value={value} onChange={onChange} tid={tid} />;
    case "signature": return <SignaturePad value={value} onChange={onChange} testId={tid} />;
    default: return null;
  }
}
